// Medicare Part B (ASP) reimbursement — low-priority block at the bottom of the
// product detail page. Collapsed by default in all states. Three states:
// covered · not_covered · pending. TSX port of the mockup's MedicarePartB.
import React from 'react';
import { PartBData, PartBProduct, PartBHistoryRow, PartBAnomaly } from '../../types/products';
import { C, MONO, ANOMALY_LABEL, fmtQuarter } from './chronologyTokens';

// Sub-dollar ASPs (e.g. $0.0358) need more precision than 2 decimals, else
// distinct prices collapse to the same "$0.04".
function fmtAsp(n?: number | null): string {
  if (n == null) return '—';
  return `$${Math.abs(n) < 1 ? n.toFixed(4) : n.toFixed(2)}`;
}

// ── Trend glyph (monochrome by direction) ──────────────────────────────────
function TrendMark({ direction, value }: { direction?: string | null; value?: number | null }) {
  if (direction === 'new') {
    return (
      <span style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, color: '#FFFFFF', background: C.mute, padding: '2px 6px', borderRadius: 3, letterSpacing: '0.1em' }}>
        NEW
      </span>
    );
  }
  const map: Record<string, { glyph: string; color: string }> = {
    up: { glyph: '↑', color: '#0F8A4E' },
    down: { glyph: '↓', color: C.red },
    flat: { glyph: '→', color: C.mute },
  };
  const m = map[direction || 'flat'] || map.flat;
  const v = value == null ? '' : `${value > 0 ? '+' : ''}${value.toFixed(2)} %`;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: m.color, fontFamily: MONO, fontSize: 12, fontWeight: 600 }}>
      <span style={{ fontSize: 13, lineHeight: 1 }}>{m.glyph}</span>
      <span>{v} QoQ</span>
    </span>
  );
}

// ── Anomaly chip ────────────────────────────────────────────────────────────
function AnomalyChip({ code, severity }: PartBAnomaly) {
  const palette: Record<string, { fg: string; bg: string; brd: string }> = {
    P1: { fg: C.red, bg: C.redBg, brd: 'rgba(200,22,10,0.22)' },
    P2: { fg: C.gold, bg: C.goldBg, brd: 'rgba(212,160,23,0.30)' },
    P3: { fg: C.mute, bg: C.greyBg, brd: 'rgba(106,95,82,0.20)' },
  };
  const p = palette[severity] || palette.P3;
  const label = ANOMALY_LABEL[code] || code;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 8px 3px 7px', borderRadius: 999, background: p.bg, border: `1px solid ${p.brd}`, color: p.fg, fontSize: 11, fontWeight: 600, lineHeight: 1.4, whiteSpace: 'nowrap' }}>
      <span style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', opacity: 0.85 }}>{severity}</span>
      <span style={{ width: 1, height: 9, background: p.fg, opacity: 0.25 }} />
      {label}
    </span>
  );
}

// ── Pill badge (top-right of collapsed header) ──────────────────────────────
function PartBBadge({ text }: { text: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 9px', borderRadius: 999, background: C.cream, border: `1px solid ${C.border}`, color: C.mute, fontFamily: MONO, fontSize: 10, fontWeight: 600, letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
      {text}
    </span>
  );
}

// ── Body for the COVERED state ──────────────────────────────────────────────
function PartBCoveredBody({ p, history }: { p: PartBProduct; history: PartBHistoryRow[] }) {
  const [histOpen, setHistOpen] = React.useState(false);
  const tooltip = p.paymentLimit != null ? `Medicare payment limit: ${fmtAsp(p.paymentLimit)} / unit` : 'Medicare payment limit';
  return (
    <div>
      {/* Priced row */}
      <div style={{ padding: '14px 18px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 18, alignItems: 'start' }}>
          {/* Left: identifiers + chips */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
            <div style={{ fontFamily: MONO, fontSize: 11, color: C.mute, letterSpacing: '0.02em', display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              {p.ndc && <span><span style={{ color: C.dim }}>NDC</span> <span style={{ color: C.ink, fontWeight: 600 }}>{p.ndc}</span></span>}
              {p.hcpcs && <><span style={{ color: C.dim }}>·</span><span><span style={{ color: C.dim }}>HCPCS</span> <span style={{ color: C.ink, fontWeight: 600 }}>{p.hcpcs}</span></span></>}
              {p.dosage && <><span style={{ color: C.dim }}>·</span><span><span style={{ color: C.dim }}>Dosage</span> <span style={{ color: C.ink, fontWeight: 600 }}>{p.dosage}</span></span></>}
            </div>
            {p.anomalies.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {p.anomalies.map((a, i) => <AnomalyChip key={i} code={a.code} severity={a.severity} />)}
              </div>
            )}
          </div>

          {/* Right: ASP + trend */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, minWidth: 180 }}>
            <span title={tooltip} style={{ fontFamily: MONO, fontSize: 22, fontWeight: 700, color: C.ink, lineHeight: 1, letterSpacing: '-0.01em', cursor: 'help', borderBottom: `1px dotted ${C.dim}`, paddingBottom: 1 }}>
              {fmtAsp(p.asp)}
              {p.unit && <span style={{ fontSize: 12, color: C.mute, fontWeight: 500 }}> / {p.unit}</span>}
            </span>
            <TrendMark direction={p.trend} value={p.qoqPct} />
          </div>
        </div>
      </div>

      {/* History toggle */}
      {history.length > 0 && (
        <div style={{ borderTop: `1px solid ${C.line}` }}>
          <button onClick={() => setHistOpen(o => !o)} style={{ all: 'unset', cursor: 'pointer', width: '100%', padding: '9px 18px', display: 'flex', alignItems: 'center', gap: 8, background: histOpen ? C.soft : 'transparent', transition: 'background 0.15s', boxSizing: 'border-box' }}>
            <span style={{ fontFamily: MONO, fontSize: 10, fontWeight: 600, color: C.mute, letterSpacing: '0.08em' }}>VIEW FULL HISTORY</span>
            <span style={{ fontFamily: MONO, fontSize: 10, color: C.dim }}>· last {history.length} quarter{history.length !== 1 ? 's' : ''}</span>
            <span style={{ flex: 1 }} />
            <span style={{ color: C.mute, fontSize: 10, transform: histOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.18s ease' }}>▾</span>
          </button>

          {histOpen && (
            <div style={{ borderTop: `1px solid ${C.line}`, background: C.bg, animation: 'chrSlideUp 0.18s ease' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: C.soft }}>
                    {[
                      { k: 'q', l: 'Quarter', align: 'left' as const },
                      { k: 'p', l: '$/unit', align: 'right' as const },
                      { k: 't', l: 'Trend', align: 'center' as const },
                      { k: 'd', l: 'Δ %', align: 'right' as const },
                    ].map(h => (
                      <th key={h.k} style={{ textAlign: h.align, padding: '8px 18px', fontFamily: MONO, fontSize: 9, fontWeight: 600, color: C.mute, textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: `1px solid ${C.border}` }}>{h.l}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {history.map((r, i) => {
                    const arrow = r.trend === 'down' ? '↓' : r.trend === 'up' ? '↑' : '→';
                    const arrowColor = r.trend === 'down' ? C.red : r.trend === 'up' ? '#0F8A4E' : C.mute;
                    return (
                      <tr key={r.q} style={{ background: i % 2 ? C.soft : C.bg, borderBottom: i < history.length - 1 ? `1px solid ${C.line}` : 'none' }}>
                        <td style={{ padding: '7px 18px', fontFamily: MONO, color: C.ink }}>{r.q}</td>
                        <td style={{ padding: '7px 18px', fontFamily: MONO, color: C.ink, textAlign: 'right' }}>{fmtAsp(r.price)}</td>
                        <td style={{ padding: '7px 18px', fontFamily: MONO, color: arrowColor, textAlign: 'center', fontWeight: 700 }}>{arrow}</td>
                        <td style={{ padding: '7px 18px', fontFamily: MONO, color: arrowColor, textAlign: 'right' }}>{r.delta != null ? `${r.delta > 0 ? '+' : ''}${r.delta.toFixed(2)} %` : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Accordion card ──────────────────────────────────────────────────────────
export default function MedicarePartB({ data, defaultOpen = false }: { data?: PartBData | null; defaultOpen?: boolean }) {
  const [open, setOpen] = React.useState(!!defaultOpen);
  if (!data) return null;

  const badgeText =
    data.state === 'covered' ? (fmtQuarter(data.history?.[0]?.q) || 'Covered')
    : data.state === 'pending' ? 'Pending data refresh'
    : 'Not a Part B drug';

  return (
    <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 2px rgba(26,20,16,0.04)' }}>
      <button onClick={() => setOpen(o => !o)} aria-expanded={open} style={{ all: 'unset', cursor: 'pointer', width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', background: open ? C.soft : C.bg, borderBottom: open ? `1px solid ${C.line}` : 'none', transition: 'background 0.15s', boxSizing: 'border-box' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>Medicare Reimbursement (Part B ASP)</span>
        <PartBBadge text={badgeText} />
        <span style={{ flex: 1 }} />
        <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" style={{ color: C.mute, transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.18s ease', flexShrink: 0 }}>
          <path d="M3 5 L7 9 L11 5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div style={{ animation: 'chrSlideUp 0.18s ease' }}>
          <div style={{ padding: '10px 18px', fontSize: 11, color: C.dim, lineHeight: 1.5, borderBottom: `1px solid ${C.line}`, background: C.soft }}>
            Medicare payment limit (ASP + 6 %). Not the actual transaction price.
          </div>

          {data.state === 'covered' && data.product && (
            <PartBCoveredBody p={data.product} history={data.history || []} />
          )}

          {(data.state === 'not_covered' || data.state === 'pending') && (
            <div style={{ padding: '16px 18px' }}>
              <p style={{ fontSize: 12, color: C.mute, lineHeight: 1.55, margin: 0, maxWidth: 640 }}>
                {data.state === 'pending'
                  ? 'Medicare Part B ASP data for this drug is pending a refresh.'
                  : 'This drug is not in the CMS Medicare Part B crosswalk (typically Part D / OTC / non-reimbursable).'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
